import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import {
  ArrowRight, ArrowUpRight, Clock, Sparkles, Filter, BookOpen, Compass,
  CheckCircle2, Users, HeartHandshake, ShieldCheck, Zap, Mail,
} from "lucide-react";
import { toast } from "sonner";
import {
  CATEGORIES, POSTS, AUDIENCE_SECTIONS, HUB_FAQS,
  getByAudience, getByCategory,
} from "../../data/blogData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Icon map for the 4 audience sections */
const AUDIENCE_ICONS = {
  employer: Users,
  clinician: HeartHandshake,
  compliance: ShieldCheck,
  prn: Zap,
};

const RESOURCE_KITS = [
  {
    Icon: BookOpen,
    t: "PRN procurement packet",
    d: "8-page PDF with SLA boilerplate, fill-rate expectations, and model contract terms used by 200+ facilities.",
    href: "/capability-statement",
    cta: "Build capability statement",
  },
  {
    Icon: Compass,
    t: "Travel comp calculator",
    d: "An interactive calculator that breaks down taxable base, stipends, and blended OT — so clinicians compare two offers apples-to-apples.",
    href: "/dashboard/calculator",
    cta: "Open calculator",
  },
  {
    Icon: CheckCircle2,
    t: "Skills checklists (all disciplines)",
    d: "70+ specialties across Nurse, Therapist, and Allied — self-rate, attest, and submit for recruiter review.",
    href: "/compliance/checklists",
    cta: "Open checklist hub",
  },
];

export default function Blog() {
  const location = useLocation();
  const [category, setCategory] = useState("all");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  /* Deep-link support: /blog?category=<slug> */
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const c = qs.get("category");
    if (c && CATEGORIES.some((x) => x.slug === c)) setCategory(c);
  }, [location.search]);

  const filtered = useMemo(() => getByCategory(category), [category]);
  const featured = filtered.find((p) => p.featured) || filtered[0];
  const rest = filtered.filter((p) => p !== featured);

  const subscribe = async (e) => {
    e.preventDefault();
    if (subscribing || !email.trim()) return;
    setSubscribing(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const res = await fetch(`/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          name: "Blog subscriber",
          email: email.trim(),
          subject: "Newsletter subscription",
          message: "Newsletter subscription from the TRITAL Care blog.",
          source: "blog-newsletter",
          idempotencyKey: requestId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error("Subscription failed");
      }
      toast.success("You're on the list.", {
        description: "Expect one briefing per month — no sub-folders of spam.",
      });
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe right now", {
        description: "Please try again in a moment or email info@tritalcare.com.",
      });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <PageMeta
        title="Resources & workforce insights | TRITAL Care®"
        description="Research briefs, field notes and practical playbooks on clinical workforce strategy, compliance posture, travel nursing, PRN economics and clinician careers — from TRITAL Care® (TOPTAL Care Inc.)."
        canonical="https://www.tritalcare.com/blog"
      />
      <PublicNav />

      {/* 1 · HERO ------------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="blog-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-16 lg:pt-20 lg:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">Resources &amp; Insights</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              Workforce insights,
              <span className="block text-trital-navy">from the field.</span>
            </h1>
            <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed text-lg">
              Research briefs, field notes and practical playbooks on clinical workforce strategy,
              compliance posture, PRN economics, travel nursing, and clinician careers — written by operators
              who still pick up 3&nbsp;a.m. calls.
            </p>
          </div>

          {/* Category filter */}
          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="blog-categories">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 pr-1">
              <Filter className="w-3 h-3" strokeWidth={1.8} /> Filter
            </span>
            {CATEGORIES.map((c) => {
              const active = category === c.slug;
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCategory(c.slug)}
                  aria-pressed={active}
                  className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${
                    active
                      ? "bg-trital-navy text-white border border-trital-navy"
                      : "bg-white text-slate-700 border border-slate-300 hover:border-trital-navy hover:text-trital-navy"
                  }`}
                  data-testid={`blog-cat-${c.slug}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* 2 · FEATURED + GRID ------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20" data-testid="blog-grid">
        {featured && <FeaturedCard post={featured} />}

        {rest.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {rest.map((p, i) => (
              <ArticleCard key={p.slug} post={p} i={i} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="blog-empty">
            <div className="font-display font-bold text-[18px] text-slate-900">Nothing published in this category yet.</div>
            <p className="mt-2 text-[13.5px] text-slate-600">Browse all briefs or subscribe below to get new pieces the morning they ship.</p>
            <button
              onClick={() => setCategory("all")}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold hover:bg-trital-navy-deep"
            >
              Show all resources <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
          </div>
        )}
      </section>

      {/* 3 · AUDIENCE SECTIONS (Employer / Clinician / Compliance / PRN) */}
      {AUDIENCE_SECTIONS.map((section, i) => (
        <AudienceSection key={section.key} section={section} reverse={i % 2 === 1} />
      ))}

      {/* 4 · RESOURCE KITS --------------------------------------- */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="blog-kits">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div className="overline text-trital-red">Resource kits</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              Tools you can use, today.
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
              Turn what you read into what you ship — interactive tools and packet templates built for
              procurement teams, recruiters, and clinicians.
            </p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {RESOURCE_KITS.map(({ Icon, t, d, href, cta }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card flex flex-col"
                data-testid={`blog-kit-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-trital-navy text-white flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-5 font-display font-bold text-[17px] text-slate-900">{t}</div>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed flex-1">{d}</p>
                <Link
                  to={href}
                  className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-trital-navy hover:gap-2 transition-all"
                  data-testid={`blog-kit-cta-${i}`}
                >
                  {cta} <ArrowUpRight className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 · NEWSLETTER + FAQ ------------------------------------ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-2 gap-10" data-testid="blog-newsletter-faq">
        <form onSubmit={subscribe} className="rounded-3xl bg-trital-navy-ink text-white relative overflow-hidden p-8 lg:p-10" data-testid="blog-newsletter-form">
          <div className="absolute inset-0 bg-trital-navy-mesh opacity-80 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-white/70">
              <Mail className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span className="overline text-white/60">Subscribe to the brief</span>
            </div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] tracking-tight text-white text-balance leading-tight">
              Workforce-strategy notes, delivered.
            </h2>
            <p className="mt-3 text-white/75 max-w-[52ch] text-[14px] leading-relaxed">
              A monthly briefing on clinical labor, compliance updates, and the data healthcare operators
              actually need. No spam. Unsubscribe in one click.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <label htmlFor="blog-newsletter-input" className="sr-only">Email address</label>
              <input
                id="blog-newsletter-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@hospital.org"
                className="flex-1 px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:bg-white/15 focus:border-white/40 backdrop-blur text-[14px]"
                data-testid="blog-newsletter-input"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white text-trital-navy font-semibold text-[13.5px] hover:bg-slate-100 disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="blog-newsletter-submit"
              >
                {subscribing ? "Subscribing…" : <>Subscribe <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>}
              </button>
            </div>
            <div className="mt-3 text-[11px] text-white/50">Zero promotional emails. One briefing per month. Your address stays with us.</div>
          </div>
        </form>

        <div data-testid="blog-faq">
          <div className="overline text-trital-red">FAQ</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] tracking-tight text-slate-900 leading-tight text-balance">
            What editors and readers ask most.
          </h2>
          <ul className="mt-6 space-y-3">
            {HUB_FAQS.map((f, i) => (
              <motion.li
                key={f.q}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card"
                data-testid={`blog-faq-${i}`}
              >
                <div className="font-display font-bold text-[15.5px] text-slate-900 leading-snug">{f.q}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{f.a}</p>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/* ==================================================================
 *  Sub-components
 * ================================================================== */

function FeaturedCard({ post }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-3xl border border-slate-200 bg-white shadow-navy p-7 lg:p-10 grid lg:grid-cols-[2fr_1fr] gap-8 relative overflow-hidden"
      data-testid="blog-featured"
    >
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-trital-medical-blue-soft blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-trital-red text-white text-[10px] font-bold uppercase tracking-wider">Featured</span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[10px] font-bold uppercase tracking-wider">{post.categoryLabel}</span>
          <span className="text-[11px] text-slate-500 font-medium">· {post.date}</span>
        </div>
        <h2 className="mt-4 font-display font-black text-3xl lg:text-[40px] leading-[1.1] text-slate-900 text-balance">
          {post.title}
        </h2>
        <p className="mt-4 text-slate-600 max-w-[64ch] text-[15.5px] leading-relaxed">{post.excerpt}</p>
        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <Link
            to={`/blog/${post.slug}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep"
            data-testid={`blog-featured-read-${post.slug}`}
          >
            Read the brief <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </Link>
          <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" strokeWidth={1.8} /> {post.read}
          </span>
        </div>
      </div>
      <div className="hidden lg:flex relative items-end justify-end">
        <div className="w-full aspect-[4/5] rounded-2xl bg-gradient-to-br from-trital-navy via-[#0f2a55] to-trital-navy-ink relative overflow-hidden border border-trital-navy/20 shadow-navy">
          <div className="absolute inset-0 dot-grid opacity-30" />
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">TRITAL Care · Resources</div>
            <div className="mt-1 font-display font-black text-[18px] leading-tight text-balance">{post.categoryLabel}</div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function ArticleCard({ post, i = 0, compact = false }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.35, delay: i * 0.03 }}
      className={`group rounded-2xl border border-slate-200 bg-white ${
        compact ? "p-5" : "p-6"
      } shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all flex flex-col`}
      data-testid={`blog-card-${post.slug}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Link
          to={`/blog?category=${post.category}`}
          className="inline-flex items-center px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[10px] font-bold uppercase tracking-wider hover:bg-trital-navy hover:text-white transition-colors"
          data-testid={`blog-card-category-${post.slug}`}
        >
          {post.categoryLabel}
        </Link>
        <span className="text-[10.5px] text-slate-500">· {post.date}</span>
      </div>
      <h3 className={`mt-3 font-display font-bold ${compact ? "text-[17px]" : "text-[19px]"} text-slate-900 leading-snug text-balance`}>
        <Link to={`/blog/${post.slug}`} className="hover:text-trital-navy">
          {post.title}
        </Link>
      </h3>
      <p className="mt-2.5 text-[13.5px] text-slate-600 leading-relaxed flex-1 line-clamp-4">{post.excerpt}</p>
      <div className="mt-5 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 inline-flex items-center gap-1.5">
          <Clock className="w-3 h-3" strokeWidth={1.8} /> {post.read}
        </span>
        <Link
          to={`/blog/${post.slug}`}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-trital-navy group-hover:gap-2 transition-all"
          data-testid={`blog-card-read-${post.slug}`}
        >
          Read <ArrowUpRight className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
        </Link>
      </div>
    </motion.article>
  );
}

function AudienceSection({ section, reverse }) {
  const posts = getByAudience(section.key, 3);
  const Icon = AUDIENCE_ICONS[section.key] || Users;
  if (!posts.length) return null;

  return (
    <section
      className={`${reverse ? "bg-slate-50 border-y border-slate-200" : "bg-white"}`}
      data-testid={`blog-audience-${section.key}`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Icon className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
              <span className="overline text-trital-navy">{section.eyebrow}</span>
            </div>
            <h2 className="mt-4 font-display font-black text-3xl lg:text-[40px] tracking-tight text-slate-900 text-balance leading-[1.1]">
              {section.title}
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">{section.blurb}</p>
          </div>
          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            <Link
              to={section.cta.href}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-300 text-trital-navy text-[13px] font-semibold hover:border-trital-navy hover:bg-slate-50"
              data-testid={`blog-audience-${section.key}-cta`}
            >
              {section.cta.label} <ArrowUpRight className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {posts.map((p, i) => <ArticleCard key={p.slug} post={p} i={i} compact />)}
        </div>
      </div>
    </section>
  );
}
